import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactECharts from 'echarts-for-react';

import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    Snackbar,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';

import BarChartIcon from '@mui/icons-material/BarChart';

import Sidebar from '../../Components/Sidebar/Sidebar';
import Navbar from '../../Components/Navbar/Navbar';
import DateInput from '../../Components/DateInput/DateInput';

import { ordersAPI, productAPI, categoryAPI } from '../../services/apiService';

import { aggregateSales } from './salesComparisonUtils';
import './SalesComparison.css';

const unwrapList = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.results)) return value.results;
    if (Array.isArray(value?.data)) return value.data;
    return [];
};

const productName = (product) => product?.name || product?.title || String(product?.id ?? '');

const PALETTE = ['#365f4c', '#e7a04a', '#4d8bbd', '#9b6ac7', '#d96c73', '#45a8a1', '#8e9e45'];

// Format chart labels according to the selected language.
// Aggregation periods are still Gregorian.
const formatChartPeriod = (period, grouping, isFa) => {
    const parts = String(period).split('-').map(Number);

    const year = parts[0];
    const month = parts[1];
    const day = grouping === 'month' ? 1 : parts[2];

    if (!year || !month || !day) return period;

    const date = new Date(Date.UTC(year, month - 1, day, 12));

    if (Number.isNaN(date.getTime())) return period;

    const locale = isFa ? 'fa-IR-u-ca-persian' : 'en-US-u-ca-gregory';

    const options =
        grouping === 'month'
            ? {
                  year: 'numeric',
                  month: 'long',
                  timeZone: 'UTC',
              }
            : {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  timeZone: 'UTC',
              };

    return new Intl.DateTimeFormat(locale, options).format(date);
};

export default function SalesComparison() {
    const { t, i18n } = useTranslation();

    const isFa = (i18n.resolvedLanguage || i18n.language || 'en').startsWith('fa');

    const locale = isFa ? 'fa-IR' : 'en-US';

    const tr = (key, english, persian) =>
        t(`sales_comparison.${key}`, {
            defaultValue: isFa ? persian : english,
        });

    // -----------------------------------------
    // STATE
    // -----------------------------------------

    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);

    // Category controls only which products are visible.
    const [category, setCategory] = useState('');

    // Global selection across ALL categories.
    const [selectedIds, setSelectedIds] = useState([]);

    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const [grouping, setGrouping] = useState('month');
    const [metric, setMetric] = useState('quantity');

    const [filterLoading, setFilterLoading] = useState(true);
    const [loading, setLoading] = useState(false);

    const [report, setReport] = useState(null);
    const [error, setError] = useState('');

    const numberFormatter = useMemo(
        () =>
            new Intl.NumberFormat(locale, {
                maximumFractionDigits: 2,
            }),
        [locale]
    );

    const resetReport = () => setReport(null);

    // -----------------------------------------
    // LOAD PRODUCTS AND CATEGORIES
    // -----------------------------------------

    useEffect(() => {
        let active = true;

        const loadFilters = async () => {
            try {
                const [productResult, categoryResult] = await Promise.all([
                    productAPI.getAll({ force: true }),
                    categoryAPI.getAll({ force: true }),
                ]);

                if (!active) return;

                setProducts(unwrapList(productResult));
                setCategories(unwrapList(categoryResult));
            } catch (err) {
                if (active) {
                    setError(
                        err.message ||
                            t('fetch_error', {
                                defaultValue: 'Failed to load products',
                            })
                    );
                }
            } finally {
                if (active) {
                    setFilterLoading(false);
                }
            }
        };

        loadFilters();

        return () => {
            active = false;
        };
    }, [t]);

    // -----------------------------------------
    // CATEGORY FILTER
    // -----------------------------------------

    const visibleProducts = useMemo(() => {
        if (!category) return products;

        return products.filter((product) => String(product.item_type) === String(category));
    }, [products, category]);

    const namesById = useMemo(
        () =>
            Object.fromEntries(
                products.map((product) => [String(product.id), productName(product)])
            ),
        [products]
    );

    // -----------------------------------------
    // MULTI-CATEGORY PRODUCT SELECTION
    // -----------------------------------------

    const handleCategoryChange = (event) => {
        // Do NOT clear selectedIds.
        // Changing the category only changes
        // which products appear in the dropdown.
        setCategory(event.target.value);
    };

    const handleProductChange = (event) => {
        const { value } = event.target;

        const incomingIds = (typeof value === 'string' ? value.split(',') : value).map(String);

        const incomingSet = new Set(incomingIds);

        // Products belonging to the visible category.
        const visibleIds = new Set(visibleProducts.map((product) => String(product.id)));

        // Preserve selected products from other categories.
        const hiddenSelections = selectedIds.filter((id) => !visibleIds.has(String(id)));

        // Keep only currently selected visible products.
        const visibleSelections = incomingIds.filter((id) => visibleIds.has(id));

        const updatedSelection = [...new Set([...hiddenSelections, ...visibleSelections])];

        setSelectedIds(updatedSelection);
        resetReport();
    };

    const handleRemoveProduct = (id) => {
        setSelectedIds((previous) =>
            previous.filter((selectedId) => String(selectedId) !== String(id))
        );

        resetReport();
    };

    const handleClearProducts = () => {
        setSelectedIds([]);
        resetReport();
    };

    // -----------------------------------------
    // GENERATE REPORT
    // -----------------------------------------

    const handleGenerate = async () => {
        if (!fromDate || !toDate || fromDate > toDate) {
            setError(
                tr(
                    'invalid_dates',
                    'Please select a valid date range.',
                    'لطفاً بازه زمانی معتبری انتخاب کنید.'
                )
            );
            return;
        }

        if (!selectedIds.length) {
            setError(
                tr(
                    'select_product',
                    'Please select at least one product.',
                    'لطفاً حداقل یک محصول انتخاب کنید.'
                )
            );
            return;
        }

        try {
            setLoading(true);
            setError('');
            setReport(null);

            const response = await ordersAPI.getReport(
                {
                    created_after: fromDate,
                    created_before: toDate,
                },
                { force: true }
            );

            if (!Array.isArray(response)) {
                throw new Error(
                    tr('load_failed', 'Failed to load sales data.', 'خطا در دریافت اطلاعات فروش.')
                );
            }

            const data = aggregateSales({
                orders: response,
                selectedItemIds: selectedIds,
                fromDate,
                toDate,
                groupBy: grouping,
                metric,
            });

            setReport({
                data,
                ids: [...selectedIds],
                grouping,
                metric,
            });
        } catch (err) {
            setError(
                err.message ||
                    tr('load_failed', 'Failed to load sales data.', 'خطا در دریافت اطلاعات فروش.')
            );
        } finally {
            setLoading(false);
        }
    };

    // -----------------------------------------
    // ECHARTS OPTIONS
    // -----------------------------------------

    const chartOptions = useMemo(() => {
        if (!report) return {};

        const chartNumberFormatter = new Intl.NumberFormat(locale, {
            maximumFractionDigits: report.metric === 'quantity' ? 0 : 2,
        });

        const labels = report.data.map((row) =>
            formatChartPeriod(row.period, report.grouping, isFa)
        );

        return {
            color: PALETTE,
            animationDuration: 350,

            textStyle: {
                fontFamily: 'inherit',
            },

            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow',
                },
                textStyle: {
                    align: isFa ? 'right' : 'left',
                },
                valueFormatter: (value) => chartNumberFormatter.format(value ?? 0),
            },

            legend: {
                type: 'scroll',
                top: 0,
                textStyle: {
                    fontFamily: 'inherit',
                },
            },

            grid: {
                top: 65,
                left: 15,
                right: 20,
                bottom: 65,
                containLabel: true,
            },

            xAxis: {
                type: 'category',
                data: labels,
                axisLabel: {
                    rotate: labels.length > 8 ? 45 : 0,
                    hideOverlap: true,
                },
            },

            yAxis: {
                type: 'value',
                min: 0,
                axisLabel: {
                    formatter: (value) => chartNumberFormatter.format(value),
                },
            },

            series: report.ids.map((id) => ({
                name: namesById[id] || id,
                type: 'bar',
                barMaxWidth: 45,
                data: report.data.map((row) => row[id] || 0),
                emphasis: {
                    focus: 'series',
                },
            })),
        };
    }, [report, namesById, locale, isFa]);

    // -----------------------------------------
    // SUMMARY TOTALS
    // -----------------------------------------

    const totals = useMemo(() => {
        if (!report) return [];

        return report.ids.map((id) => ({
            id,
            name: namesById[id] || id,
            total: report.data.reduce((sum, row) => sum + (row[id] || 0), 0),
        }));
    }, [report, namesById]);

    // -----------------------------------------
    // RENDER
    // -----------------------------------------

    return (
        <div className="sales-comparison-page" dir={isFa ? 'rtl' : 'ltr'}>
            <Sidebar />

            <div className="sales-comparison-main">
                <Navbar />

                <main className="sales-comparison-content">
                    {/* PAGE HEADER */}

                    <div className="sales-comparison-heading">
                        <Typography variant="h5" fontWeight={700}>
                            {tr('title', 'Sales Comparison', 'مقایسه فروش محصولات')}
                        </Typography>

                        <Typography color="text.secondary">
                            {tr(
                                'subtitle',
                                'Compare product sales by week or month',
                                'مقایسه عملکرد فروش محصولات به‌صورت هفتگی یا ماهانه'
                            )}
                        </Typography>
                    </div>

                    {/* FILTER PANEL */}

                    <Paper className="sales-comparison-filter" elevation={0}>
                        <div className="sales-comparison-fields">
                            {/* FROM DATE */}

                            <DateInput
                                label={tr('from', 'From date', 'از تاریخ')}
                                value={fromDate}
                                onChange={(value) => {
                                    setFromDate(value);
                                    resetReport();
                                }}
                                locale={isFa ? 'fa' : 'en'}
                                calendar={isFa ? 'persian' : 'gregorian'}
                            />

                            {/* TO DATE */}

                            <DateInput
                                label={tr('to', 'To date', 'تا تاریخ')}
                                value={toDate}
                                onChange={(value) => {
                                    setToDate(value);
                                    resetReport();
                                }}
                                locale={isFa ? 'fa' : 'en'}
                                calendar={isFa ? 'persian' : 'gregorian'}
                            />

                            {/* CATEGORY FILTER */}

                            <FormControl size="small" className="sales-comparison-field">
                                <InputLabel>{tr('category', 'Category', 'دسته‌بندی')}</InputLabel>

                                <Select
                                    label={tr('category', 'Category', 'دسته‌بندی')}
                                    value={category}
                                    disabled={filterLoading}
                                    onChange={handleCategoryChange}
                                >
                                    <MenuItem value="">
                                        {tr('all_categories', 'All categories', 'همه دسته‌بندی‌ها')}
                                    </MenuItem>

                                    {categories.map((item) => (
                                        <MenuItem key={item.id} value={String(item.id)}>
                                            {item.name || item.title}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* PRODUCT MULTI-SELECT */}

                            <FormControl
                                size="small"
                                className="sales-comparison-field sales-comparison-products"
                            >
                                <InputLabel>{tr('products', 'Products', 'محصولات')}</InputLabel>

                                <Select
                                    multiple
                                    value={selectedIds}
                                    disabled={filterLoading}
                                    onChange={handleProductChange}
                                    input={
                                        <OutlinedInput
                                            label={tr('products', 'Products', 'محصولات')}
                                        />
                                    }
                                    renderValue={(ids) =>
                                        t('sales_comparison.selected_count', {
                                            count: ids.length,
                                            defaultValue: isFa
                                                ? '{{count}} محصول انتخاب‌شده'
                                                : '{{count}} products selected',
                                        })
                                    }
                                >
                                    {visibleProducts.map((product) => {
                                        const id = String(product.id);

                                        return (
                                            <MenuItem key={id} value={id}>
                                                <Checkbox checked={selectedIds.includes(id)} />

                                                <ListItemText primary={productName(product)} />
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </div>

                        {/* SELECTED PRODUCTS */}

                        {selectedIds.length > 0 && (
                            <Box className="sales-comparison-selected">
                                <Box className="sales-comparison-selected-header">
                                    <Typography variant="body2" fontWeight={600}>
                                        {tr(
                                            'selected_products',
                                            'Selected products',
                                            'محصولات انتخاب‌شده'
                                        )}
                                    </Typography>

                                    <Typography variant="caption" color="text.secondary">
                                        {numberFormatter.format(selectedIds.length)}
                                    </Typography>
                                </Box>

                                <Box className="sales-comparison-selected-chips">
                                    {selectedIds.map((id) => (
                                        <Chip
                                            key={id}
                                            label={namesById[id] || id}
                                            size="small"
                                            className="sales-comparison-chip"
                                            onDelete={() => handleRemoveProduct(id)}
                                        />
                                    ))}

                                    <Button
                                        size="small"
                                        color="inherit"
                                        onClick={handleClearProducts}
                                    >
                                        {tr('clear_selection', 'Clear all', 'حذف همه')}
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {/* GROUPING AND METRIC */}

                        <div className="sales-comparison-choices">
                            {/* GROUP BY */}

                            <Box>
                                <Typography variant="body2" fontWeight={600} gutterBottom>
                                    {tr('group_by', 'Group by', 'گروه‌بندی زمانی')}
                                </Typography>

                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={grouping}
                                    onChange={(_, value) => {
                                        if (!value) return;

                                        setGrouping(value);
                                        resetReport();
                                    }}
                                >
                                    <ToggleButton value="week">
                                        {tr('weekly', 'Weekly', 'هفتگی')}
                                    </ToggleButton>

                                    <ToggleButton value="month">
                                        {tr('monthly', 'Monthly', 'ماهانه')}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>

                            {/* COMPARE BY */}

                            <Box>
                                <Typography variant="body2" fontWeight={600} gutterBottom>
                                    {tr('compare_by', 'Compare by', 'معیار مقایسه')}
                                </Typography>

                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={metric}
                                    onChange={(_, value) => {
                                        if (!value) return;

                                        setMetric(value);
                                        resetReport();
                                    }}
                                >
                                    <ToggleButton value="quantity">
                                        {tr('quantity', 'Quantity sold', 'تعداد فروش')}
                                    </ToggleButton>

                                    <ToggleButton value="revenue">
                                        {tr('revenue', 'Sales revenue', 'مبلغ فروش')}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>

                            {/* GENERATE BUTTON */}

                            <Button
                                className="sales-comparison-generate"
                                variant="contained"
                                startIcon={<BarChartIcon />}
                                onClick={handleGenerate}
                                disabled={loading || filterLoading}
                            >
                                {loading ? (
                                    <CircularProgress size={22} color="inherit" />
                                ) : (
                                    tr('generate', 'Generate chart', 'نمایش نمودار')
                                )}
                            </Button>
                        </div>
                    </Paper>

                    {/* SUMMARY CARDS */}

                    {report && (
                        <>
                            <div className="sales-comparison-summary">
                                {totals.map((item) => (
                                    <Paper
                                        key={item.id}
                                        className="sales-comparison-stat"
                                        elevation={0}
                                    >
                                        <Typography variant="body2" color="text.secondary">
                                            {item.name}
                                        </Typography>

                                        <Typography variant="h6" fontWeight={700}>
                                            {numberFormatter.format(item.total)}

                                            {report.metric === 'revenue'
                                                ? ` ${t('common.currency', {
                                                      defaultValue: isFa ? 'تومان' : 'Toman',
                                                  })}`
                                                : ''}
                                        </Typography>
                                    </Paper>
                                ))}
                            </div>

                            {/* CHART */}

                            <Paper className="sales-comparison-chart" elevation={0}>
                                <Typography variant="h6" gutterBottom>
                                    {report.metric === 'quantity'
                                        ? tr('quantity', 'Quantity sold', 'تعداد فروش')
                                        : tr('revenue', 'Sales revenue', 'مبلغ فروش')}
                                </Typography>

                                {report.data.length ? (
                                    <ReactECharts
                                        option={chartOptions}
                                        notMerge
                                        lazyUpdate
                                        style={{
                                            height: 440,
                                            width: '100%',
                                        }}
                                    />
                                ) : (
                                    <Alert severity="info">
                                        {tr(
                                            'no_periods',
                                            'No data is available for this date range.',
                                            'داده‌ای برای این بازه زمانی وجود ندارد.'
                                        )}
                                    </Alert>
                                )}
                            </Paper>
                        </>
                    )}

                    {/* EMPTY STATE */}

                    {!report && !loading && (
                        <Paper className="sales-comparison-placeholder" elevation={0}>
                            <BarChartIcon
                                sx={{
                                    fontSize: 50,
                                    opacity: 0.4,
                                }}
                            />

                            <Typography>
                                {tr(
                                    'empty',
                                    'Choose products and a date range, then generate the chart.',
                                    'محصولات و بازه زمانی را انتخاب کرده و نمودار را نمایش دهید.'
                                )}
                            </Typography>
                        </Paper>
                    )}

                    {/* ERROR SNACKBAR */}

                    <Snackbar
                        open={Boolean(error)}
                        autoHideDuration={5000}
                        onClose={() => setError('')}
                    >
                        <Alert severity="error" onClose={() => setError('')}>
                            {error}
                        </Alert>
                    </Snackbar>
                </main>
            </div>
        </div>
    );
}
